import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  Injectable,
  PLATFORM_ID,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from "@angular/core";
import {
  SUPPORTED_LOCALES,
  SupportedLocale,
  TranslationDictionary,
  TranslationValue,
  translations,
} from "./translations";

type LocaleDirection = "ltr" | "rtl";

type LocaleConfig = {
  nativeLabel: string;
  htmlLang: string;
  intlLocale: string;
  apiLanguage: string;
  recaptchaLanguage: string;
  direction: LocaleDirection;
};

export type LanguageOption = LocaleConfig & {
  code: SupportedLocale;
};

const DEFAULT_LOCALE: SupportedLocale = "tr";
const STORAGE_KEY = "app.locale";

const LOCALE_CONFIG: Record<SupportedLocale, LocaleConfig> = {
  tr: {
    nativeLabel: "Türkçe",
    htmlLang: "tr",
    intlLocale: "tr-TR",
    apiLanguage: "tr-TR",
    recaptchaLanguage: "tr",
    direction: "ltr",
  },
  en: {
    nativeLabel: "English",
    htmlLang: "en",
    intlLocale: "en-US",
    apiLanguage: "en-US",
    recaptchaLanguage: "en",
    direction: "ltr",
  },
  ar: {
    nativeLabel: "العربية",
    htmlLang: "ar",
    intlLocale: "ar",
    apiLanguage: "ar",
    recaptchaLanguage: "ar",
    direction: "rtl",
  },
  nl: {
    nativeLabel: "Nederlands",
    htmlLang: "nl",
    intlLocale: "nl-NL",
    apiLanguage: "nl-NL",
    recaptchaLanguage: "nl",
    direction: "ltr",
  },
  fr: {
    nativeLabel: "Français",
    htmlLang: "fr",
    intlLocale: "fr-FR",
    apiLanguage: "fr-FR",
    recaptchaLanguage: "fr",
    direction: "ltr",
  },
};

@Injectable({
  providedIn: "root",
})
export class LocaleService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly localeSignal = signal<SupportedLocale>(
    this.resolveInitialLocale(),
  );

  readonly locale: Signal<SupportedLocale> = this.localeSignal.asReadonly();
  readonly currentConfig = computed(
    () => LOCALE_CONFIG[this.localeSignal()],
  );
  readonly languageOptions: LanguageOption[] = SUPPORTED_LOCALES.map((code) => ({
    code,
    ...LOCALE_CONFIG[code],
  }));

  constructor() {
    effect(() => {
      const locale = this.localeSignal();
      const config = LOCALE_CONFIG[locale];

      this.document.documentElement.lang = config.htmlLang;
      this.document.documentElement.dir = config.direction;

      if (isPlatformBrowser(this.platformId)) {
        window.localStorage.setItem(STORAGE_KEY, locale);
      }
    });
  }

  setLocale(locale: string): void {
    if (this.isSupportedLocale(locale)) {
      this.localeSignal.set(locale);
    }
  }

  getCurrentLocale(): SupportedLocale {
    return this.localeSignal();
  }

  getIntlLocale(): string {
    return this.currentConfig().intlLocale;
  }

  getApiLanguage(): string {
    return this.currentConfig().apiLanguage;
  }

  getRecaptchaLanguage(): string {
    return this.currentConfig().recaptchaLanguage;
  }

  isRtl(): boolean {
    return this.currentConfig().direction === "rtl";
  }

  t(key: string, params?: Record<string, string | number | null | undefined>): string {
    const value = this.getValue(key);

    if (typeof value !== "string") {
      return key;
    }

    if (!params) {
      return value;
    }

    return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, paramKey: string) => {
      const paramValue = params[paramKey];
      return paramValue === null || paramValue === undefined
        ? ""
        : String(paramValue);
    });
  }

  tArray(key: string): string[] {
    const value = this.getValue(key);

    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === "string")
      : [];
  }

  getValue<T extends TranslationValue = TranslationValue>(
    key: string,
    locale: SupportedLocale = this.localeSignal(),
  ): T | undefined {
    const value =
      this.getTranslationValue(key, locale) ??
      this.getTranslationValue(key, DEFAULT_LOCALE);

    return value as T | undefined;
  }

  formatDate(
    value: string | Date | null | undefined,
    options: Intl.DateTimeFormatOptions,
  ): string {
    const date = this.toDate(value);

    if (!date) {
      return typeof value === "string" ? value : "";
    }

    return new Intl.DateTimeFormat(this.getIntlLocale(), options).format(date);
  }

  private getTranslationValue(
    key: string,
    locale: SupportedLocale,
  ): TranslationValue | undefined {
    const localeTranslations = translations[locale] as TranslationDictionary;

    return key.split(".").reduce<TranslationValue | undefined>((acc, part) => {
      if (!acc || typeof acc === "string" || Array.isArray(acc)) {
        return undefined;
      }

      return acc[part];
    }, localeTranslations);
  }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private resolveInitialLocale(): SupportedLocale {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_LOCALE;
    }

    const queryLocale = this.readLocaleFromQuery();
    if (queryLocale) {
      return queryLocale;
    }

    const storedLocale = window.localStorage.getItem(STORAGE_KEY);
    if (this.isSupportedLocale(storedLocale)) {
      return storedLocale;
    }

    const browserLocale = window.navigator.language.toLowerCase();
    const matchedLocale = SUPPORTED_LOCALES.find((locale) =>
      browserLocale.startsWith(locale),
    );

    return matchedLocale ?? DEFAULT_LOCALE;
  }

  private readLocaleFromQuery(): SupportedLocale | null {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang");

    return this.isSupportedLocale(lang) ? lang : null;
  }

  private isSupportedLocale(
    locale: string | null | undefined,
  ): locale is SupportedLocale {
    return !!locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale);
  }
}
